/**
 * Catalog API client — Settings → Catalog tab.
 *
 * Talks to `/api/catalog*`:
 *   - GET  /api/catalog            list (role-scoped)
 *   - POST /api/catalog            create (vendor: self; platform: any vendor)
 *   - POST /api/catalog/{id}       edit   (vendor: own items only)
 *   - POST /api/catalog/{id}/active activate/deactivate (same gate as edit)
 *   - GET  /api/catalog/vendors    list of vendor companies the caller can see
 */

import { ApiError, apiFetch } from "./apiClient";

export interface CatalogItem {
  item_id: number;
  sku: string | null;
  name: string | null;
  category_name: string | null;
  oem_no: string | null;
  unit_price: number | null;
  lead_time_days: number;
  lead_time_override_days: number | null;
  lead_time_set_by: string | null;
  lead_time_set_utc: string | null;
  is_critical: boolean;
  critical_set_by: string | null;
  critical_set_utc: string | null;
  /**
   * Active/inactive flag (migration 120). An inactive part is hidden from
   * Match Tags and from customer/vendor parts lists; platform users still see
   * it when the list is fetched with `includeInactive`. Distinct from delete.
   */
  is_active: boolean;
  active_set_by: string | null;
  active_set_utc: string | null;
  company_id: number | null;
  company_name: string | null;
}

export interface CatalogPage {
  total: number;
  limit: number;
  offset: number;
  items: CatalogItem[];
}

export interface VendorOption {
  company_id: number;
  company_name: string;
}

export interface CreateItemBody {
  sku: string;
  name: string;
  oem_no?: string | null;
  category_name?: string | null;
  unit_price?: number | null;
  lead_time_days?: number;
  /** Required for platform users; vendor users have it auto-applied. */
  vendor_company_id?: number;
}

export interface UpdateItemBody {
  sku?: string;
  name?: string;
  oem_no?: string | null;
  category_name?: string | null;
  unit_price?: number | null;
  lead_time_days?: number;
  vendor_company_id?: number;
}

export class CatalogApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "CatalogApiError";
  }
}

async function request<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new CatalogApiError(e.message, e.status) : e;
  }
}

export function fetchCatalog(
  token: string,
  opts: {
    q?: string;
    vendor?: number;
    limit?: number;
    offset?: number;
    /** Platform-only. Server ignores it for customer/vendor callers. */
    includeInactive?: boolean;
  } = {},
): Promise<CatalogPage> {
  const p = new URLSearchParams();
  if (opts.q) p.set("q", opts.q);
  if (opts.vendor != null) p.set("vendor", String(opts.vendor));
  if (opts.includeInactive) p.set("include_inactive", "1");
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset != null) p.set("offset", String(opts.offset));
  const qs = p.toString();
  return request<CatalogPage>(`/api/catalog${qs ? `?${qs}` : ""}`, token);
}

export async function fetchCatalogVendors(token: string): Promise<VendorOption[]> {
  const r = await request<{ vendors: VendorOption[] }>("/api/catalog/vendors", token);
  return r.vendors;
}

export function createCatalogItem(token: string, body: CreateItemBody): Promise<CatalogItem> {
  return request<CatalogItem>("/api/catalog", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateCatalogItem(
  token: string,
  itemId: number,
  body: UpdateItemBody,
): Promise<CatalogItem> {
  return request<CatalogItem>(`/api/catalog/${itemId}`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Activate or deactivate a part. Platform users may toggle any part; a vendor
 * may toggle only its own. Returns the updated row.
 */
export function setCatalogItemActive(
  token: string,
  itemId: number,
  isActive: boolean,
): Promise<CatalogItem> {
  return request<CatalogItem>(`/api/catalog/${itemId}/active`, token, {
    method: "POST",
    body: JSON.stringify({ is_active: isActive }),
  });
}

// ---------- Bulk import (Parts List mass import) ---------------------------

/** One row in the /api/catalog/import payload. Matches the template CSV
 *  headers exactly. `vendor` is required for platform callers; ignored /
 *  auto-scoped for vendor callers. */
export interface CatalogImportRow {
  sku: string;
  name: string;
  vendor?: string | null;
  oem_no?: string | null;
  category?: string | null;
  unit_price?: number | null;
  lead_time_days?: number | null;
}

export interface CatalogImportResult {
  row: number;
  sku: string | null;
  action: "created" | "updated" | "error";
  item_id?: number;
  error?: string;
}

export interface CatalogImportResponse {
  total: number;
  created: number;
  updated: number;
  errors: number;
  results: CatalogImportResult[];
}

/** Bulk-upsert parts. Server matches on (vendor_company_id, sku): an
 *  existing SKU updates in place, a new one is inserted. Caller sends
 *  <=2000 rows per call. */
export function importCatalog(
  token: string,
  rows: CatalogImportRow[],
): Promise<CatalogImportResponse> {
  return request<CatalogImportResponse>("/api/catalog/import", token, {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

/** Fetch the whole scoped catalog by walking pages. The list endpoint is
 *  bounded by role scope + optional filters, so this is what the caller
 *  can already see -- no privilege escalation. Cap keeps a runaway server
 *  from pulling the client down. */
export async function fetchAllCatalog(
  token: string,
  opts: { q?: string; vendor?: number } = {},
  pageSize = 500,
  maxRows = 20_000,
): Promise<CatalogItem[]> {
  const out: CatalogItem[] = [];
  let offset = 0;
  while (out.length < maxRows) {
    const p = await fetchCatalog(token, { ...opts, limit: pageSize, offset });
    out.push(...p.items);
    if (p.items.length < pageSize || out.length >= p.total) break;
    offset += pageSize;
  }
  return out;
}
